import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ClipboardList, Settings as SettingsIcon } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface SubSection {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  iconBg: string;
  available: boolean;
}

const SystemSettingsPage: React.FC = () => {
  const navigate = useNavigate();

  const subSections: SubSection[] = [
    {
      title: 'Judges Sheet Templates',
      description:
        'Create and manage the column layouts printed on rodeo judges sheets.',
      icon: ClipboardList,
      path: '/settings/system/judge-sheet-templates',
      iconBg: 'bg-amber-600',
      available: true,
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-8 flex items-start gap-4">
        <div className="p-3 rounded-lg bg-purple-500 text-white">
          <SettingsIcon className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-foreground">System Configuration</h1>
          <p className="mt-2 text-muted-foreground">
            Configure system-wide settings used across the application.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {subSections.map((section) => {
          const Icon = section.icon;
          return (
            <Card
              key={section.path}
              className={
                section.available
                  ? 'hover:shadow-lg transition-shadow cursor-pointer group'
                  : 'opacity-50 cursor-not-allowed relative'
              }
              onClick={() => {
                if (section.available) navigate(section.path);
              }}
            >
              {!section.available && (
                <div className="absolute top-4 right-4 z-10">
                  <Badge variant="secondary" className="text-xs">
                    Coming Soon
                  </Badge>
                </div>
              )}
              <CardHeader className="pb-3">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${section.iconBg} text-white shrink-0`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground">{section.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {section.description}
                    </p>
                  </div>
                  {section.available && (
                    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <Button
                  variant={section.available ? 'outline' : 'secondary'}
                  className="w-full"
                  disabled={!section.available}
                  tabIndex={section.available ? 0 : -1}
                >
                  {section.available ? 'Open' : 'Coming Soon'}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default SystemSettingsPage;
